/**
 * Shared data colour scale from scala_rgb_1_10.xlsx ("Scala RGB").
 * Integer anchors 1–10 go from red (worst) to green (best); every data
 * gradient in the platform (map layers, charts, legends) derives from it.
 */
export const SCORE_SCALE = [
  '#C8232D',
  '#DC4128',
  '#EB6928',
  '#F5962D',
  '#FAC337',
  '#EBD741',
  '#B4CD41',
  '#73B946',
  '#379B4B',
  '#147D41',
];

/** Same anchors ordered green (best) to red (worst), for "lower is better" metrics. */
export const SCORE_SCALE_REVERSED = [...SCORE_SCALE].reverse();

export const SCORE_SCALE_BEST = SCORE_SCALE[SCORE_SCALE.length - 1];
export const SCORE_SCALE_GOOD = SCORE_SCALE[7];
export const SCORE_SCALE_MID = SCORE_SCALE[4];
export const SCORE_SCALE_WARN = SCORE_SCALE[3];
export const SCORE_SCALE_BAD = SCORE_SCALE[1];
export const SCORE_SCALE_WORST = SCORE_SCALE[0];

/** Level of service A (best) … F (worst) on the workbook anchors. */
export const LOS_COLORS = {
  A: SCORE_SCALE[9],
  B: SCORE_SCALE[7],
  C: SCORE_SCALE[5],
  D: SCORE_SCALE[3],
  E: SCORE_SCALE[1],
  F: SCORE_SCALE[0],
};

export function losColor(grade, fallback = '#999999') {
  const key = String(grade ?? '').trim().toUpperCase();
  return LOS_COLORS[key] || fallback;
}

/** Selection highlighting shared by every map module. */
export const MAP_SELECTED_COLOR = '#8C74FF';
export const MAP_MUTED_COLOR = '#777D78';

const hexToRgb = (hex) => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
];

// The workbook stores midpoint channels with round-half-to-even.
const roundLikeWorkbook = (value) => {
  const floor = Math.floor(value);
  const fraction = value - floor;
  if (Math.abs(fraction - 0.5) < Number.EPSILON * 10) {
    return floor % 2 === 0 ? floor : floor + 1;
  }
  return Math.round(value);
};

const toHex = (value) => roundLikeWorkbook(value).toString(16).padStart(2, '0').toUpperCase();

/** Resolve any numeric 1–10 score to the workbook's continuous RGB scale. */
export function scoreScaleColor(value, fallback = '#76786C') {
  const score = Number(value);
  if (!Number.isFinite(score)) return fallback;

  const clamped = Math.min(10, Math.max(1, score));
  const lowerIndex = Math.floor(clamped) - 1;
  const upperIndex = Math.min(SCORE_SCALE.length - 1, lowerIndex + 1);
  const fraction = clamped - Math.floor(clamped);
  const lower = hexToRgb(SCORE_SCALE[lowerIndex]);
  const upper = hexToRgb(SCORE_SCALE[upperIndex]);
  const channels = lower.map((channel, index) => (
    channel + ((upper[index] - channel) * fraction)
  ));
  return `#${channels.map(toHex).join('')}`;
}

/** 0 → red, 1 → green. Use for "higher is better" metrics (speed, score). */
export function goodnessColor(ratio, fallback) {
  const t = Number(ratio);
  if (!Number.isFinite(t)) return fallback ?? '#76786C';
  return scoreScaleColor(1 + Math.min(1, Math.max(0, t)) * 9, fallback);
}

/** 0 → green, 1 → red. Use for "higher is worse" metrics (flow, V/C, delay). */
export function badnessColor(ratio, fallback) {
  const t = Number(ratio);
  if (!Number.isFinite(t)) return fallback ?? '#76786C';
  return goodnessColor(1 - Math.min(1, Math.max(0, t)), fallback);
}

/** Map a value inside [min, max] onto the scale. */
export function rangeColor(value, min, max, { reverse = false, fallback } = {}) {
  const span = Number(max) - Number(min);
  const t = span > 0 ? (Number(value) - Number(min)) / span : 0;
  return reverse ? badnessColor(t, fallback) : goodnessColor(t, fallback);
}

/** CSS gradient using every workbook anchor. */
export function scoreScaleGradient(direction = 'to right', { reverse = false } = {}) {
  const colors = reverse ? SCORE_SCALE_REVERSED : SCORE_SCALE;
  return `linear-gradient(${direction}, ${colors.join(', ')})`;
}

export function hexWithAlpha(hex, alpha) {
  const [red, green, blue] = hexToRgb(hex);
  return `rgba(${red},${green},${blue},${alpha})`;
}

/**
 * Colour for a map element given the current selection: purple when selected,
 * grey when another element is selected, its own colour when nothing is.
 */
export function selectionAwareColor(isSelected, hasSelection, normalColor) {
  if (!hasSelection) return normalColor;
  return isSelected ? MAP_SELECTED_COLOR : MAP_MUTED_COLOR;
}
